import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { DashboardLayout } from '@shared/infrastructure/ui/layout/dashboardLayout';
import {
  TablePlain,
  TableCell,
  TableRow
} from '@shared/infrastructure/ui/components/table/tablePlain/tablePlain';
import { useAppSelector } from '@shared/infrastructure/state/store';
import {
  countryCodeSelector,
  languageCodeSelector
} from '@shared/infrastructure/state/global.slice';
import { PageHeader } from '@shared/infrastructure/ui/components/header/pageHeader';
import EditIcon from '@mui/icons-material/CreateOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import AddIcon from '@mui/icons-material/Add';
import DriveFolderUploadIcon from '@mui/icons-material/DriveFolderUpload';
import Snackbar from '@mui/material/Snackbar';
import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Fab,
  IconButton,
  Tooltip
} from '@mui/material';
import { historyRepository } from '@shared/domain/router/router.repository';
import { ROUTES } from '@shared/infrastructure/router/paths';
import { routeRepository } from '@shared/domain/router/route/route.repository';
import { Content, ContentKind } from '@shared/domain/content/content.aggregate';
import { contentRepository } from '@shared/domain/content/content.repository';
import { HorizontalSpaceBetweenLayout } from '@shared/infrastructure/ui/layout/horizontalSpaceBetweenLayout';

const Row = ({
  content,
  countryCode,
  onDelete,
  setSnackbarMessage,
  setSnackbarOpen
}: {
  content: Content;
  countryCode: string;
  onDelete: () => void;
  setSnackbarMessage: (value: any) => void;
  setSnackbarOpen: (value: any) => void;
}) => {
  const acceptByKind = (kind: ContentKind) => {
    switch (kind) {
      case 'Document':
        return 'application/pdf';
      case 'Video':
        return 'video/mp4';
      case 'Game':
        throw new Error('Game is not supported as accept-type.');
      default:
        throw new Error(`Not implemented ContentKind provided: ${kind}`);
    }
  };

  return (
    <TableRow
      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
      aria-label='contents table row'
    >
      <TableCell component='th' scope='row'>
        {content.name}
      </TableCell>
      <TableCell>{content.nameLanguage}</TableCell>
      <TableCell>{content.kind}</TableCell>
      <TableCell>
        <Tooltip title='Edit content' placement='left-start'>
          <IconButton
            size='large'
            aria-label='Edit content'
            onClick={() =>
              historyRepository.push(
                routeRepository.buildRoute(ROUTES.CONTENTS_EDIT.path, {
                  contentId: String(content.id)
                })
              )
            }
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title='Delete content' placement='left-start'>
          <IconButton
            size='large'
            aria-label='Delete content'
            onClick={onDelete}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
        {content.kind !== 'Game' ? (
          <Tooltip title='Replace content file' placement='left-start'>
            <IconButton
              size='large'
              aria-label='Replace content file'
              component='label'
            >
              <DriveFolderUploadIcon></DriveFolderUploadIcon>
              <input
                type='file'
                accept={acceptByKind(content.kind)}
                hidden
                onChange={(e) => {
                  if (e.target && e.target.files && e.target.files[0]) {
                    setSnackbarMessage('Your movie-file is being uploaded ...');
                    setSnackbarOpen(true);
                    contentRepository
                      .uploadFile(
                        content.id,
                        countryCode,
                        e.target.files[0],
                        content.kind
                      )
                      .then(() => {
                        setSnackbarMessage('Movie-file uploaded.');
                        setSnackbarOpen(true);
                      })
                      .catch(() => {
                        setSnackbarMessage('Error uploading the movie-file!');
                        setSnackbarOpen(true);
                      });
                  }
                }}
              />
            </IconButton>
          </Tooltip>
        ) : (
          <div></div>
        )}
      </TableCell>
    </TableRow>
  );
};

const DeleteDialog = ({
  content,
  onCancel,
  onAccept
}: {
  content?: Content;
  onCancel?: () => void;
  onAccept?: (content: Content) => void;
}) => {
  return (
    <Dialog
      open={!!content}
      onClose={onCancel}
      aria-labelledby='delete-dialog-title'
      aria-describedby='delete-dialog-description'
    >
      <DialogTitle id='delete-dialog-title'>Are you sure?</DialogTitle>
      <DialogContent>
        <DialogContentText id='delete-dialog-description'>
          The content will be permanently lost.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={onCancel} autoFocus>
          Cancel
        </Button>
        <Button
          variant='contained'
          color='error'
          onClick={() => onAccept?.(content!)}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const ContentView = () => {
  const countryCode = useAppSelector(countryCodeSelector);
  const languageCode = useAppSelector(languageCodeSelector);
  const [contents, setContents] = useState<Content[]>([]);
  const [deleteContent, setDeleteContent] = useState<Content>();
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState('');

  useEffect(() => {
    contentRepository
      .getContents(countryCode, languageCode)
      .then((contents) =>
        setContents(
          contents.sort((a: Content, b: Content) =>
            a.name > b.name ? 1 : b.name > a.name ? -1 : 0
          ) || []
        )
      );
  }, [countryCode, languageCode]);

  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === 'clickaway') {
      return;
    }

    setSnackbarOpen(false);
  };

  const snackbarAction = (
    <React.Fragment>
      <IconButton
        size='small'
        aria-label='close'
        color='inherit'
        onClick={handleSnackbarClose}
      >
        <CloseIcon fontSize='small' />
      </IconButton>
    </React.Fragment>
  );

  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        action={snackbarAction}
        message={snackbarMessage}
      />
      <Fab
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        color='primary'
        aria-label='Create'
        onClick={() =>
          historyRepository.push(
            routeRepository.buildRoute(ROUTES.CONTENTS_CREATE.path, {})
          )
        }
      >
        <AddIcon />
      </Fab>
      <DashboardLayout>
        <HorizontalSpaceBetweenLayout>
          <>
            <PageHeader>
              <>Contents View</>
            </PageHeader>
          </>
        </HorizontalSpaceBetweenLayout>
        <TablePlain<Content[]>
          data={contents}
          headers={['Identifier', 'Displayname', 'Type', '']}
        >
          {(data: Content[]) =>
            data.map((content: Content) => (
              <Row
                key={content.id}
                countryCode={countryCode}
                content={content}
                onDelete={() => setDeleteContent(content)}
                setSnackbarOpen={setSnackbarOpen}
                setSnackbarMessage={setSnackbarMessage}
              ></Row>
            ))
          }
        </TablePlain>
        <DeleteDialog
          content={deleteContent}
          onCancel={() => setDeleteContent(undefined)}
          onAccept={(content) => {
            contentRepository
              .deleteContent(countryCode, content.id)
              .then(() => {
                setContents((contents) =>
                  contents.filter((s) => s.id !== content.id)
                );
                setDeleteContent(undefined);
              });
          }}
        />
      </DashboardLayout>
    </>
  );
};
